import { Radio as MantineRadio, Select } from '@mantine/core'
import { NumberInput } from 'components/NumberInput/NumberInput'
import { Radio } from 'components/Radio/Radio'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect } from 'react'
import { fadeInOutStylesMaker } from 'utils/fadeInOutStylesMaker'
import type { WeatherForecastAlertOnly } from '../../../../_types'
import { getErrorStyle } from '../../../../_utils/getErrorStyle'
import { AlertsValidators, isNumberOrNumericString } from '../../../../AlertValidators/AlertValidators'
import { ErrorTextWrapper } from '../../../../components/ErrorTextWrapper'
import { TranslateComponentWrapper } from '../../../../components/TranslateComponentWrapper'
import { TranslatePhraseWrapper } from '../../../../components/TranslatePhraseWrapper'
import { AlertsSettings, unitSpeedDisplay, unitTempDisplay } from '../../../../settings/alertSettings'
import { helpTextErrorHelper } from '../../_utils/helpTextErrorHelper'
import type { WeatherForecastAlertFormValues } from '../../WeatherForecastAlertForm'

const thresholdTypes = AlertsSettings.weatherForecastAlertThresholdItems

const getRuleType = (
  rule: Partial<WeatherForecastAlertFormValues['rules']>,
): (typeof thresholdTypes)[keyof typeof thresholdTypes] => {
  if (!rule || typeof rule !== 'object') return thresholdTypes.WIND

  const keys = Object.keys(rule).map((key) => key.toLowerCase())
  const matchingType = Object.values(thresholdTypes).find((type) => keys.some((key) => key.includes(type)))

  return matchingType || thresholdTypes.WIND
}

const getRuleValue = (rule: Partial<WeatherForecastAlertFormValues['rules']>): number | undefined => {
  if (!rule) return undefined

  if ('windGreaterThanOrEqualToUserUnits' in rule) {
    return rule.windGreaterThanOrEqualToUserUnits
  }

  if ('temperatureLessThanOrEqualToUserUnits' in rule) {
    return rule.temperatureLessThanOrEqualToUserUnits
  }

  if ('temperatureGreaterThanOrEqualToUserUnits' in rule) {
    return rule.temperatureGreaterThanOrEqualToUserUnits
  }

  return undefined
}

const getTemperatureOperator = (rule: Partial<WeatherForecastAlertFormValues['rules']>): '>=' | '<=' => {
  if (!rule) return '>='

  if ('temperatureGreaterThanOrEqualToUserUnits' in rule) {
    return '>='
  }

  if ('temperatureLessThanOrEqualToUserUnits' in rule) {
    return '<='
  }

  return '>=' // Default operator if none is set
}

export const WeatherForecastThreshold = ({
  rule,
  setRule,
  hourOfDay,
  setHourOfDay,
  setThresholdIsValid,
  triedToSubmit,
}: {
  rule: Partial<WeatherForecastAlertOnly['rules']>
  setRule: Dispatch<SetStateAction<Partial<WeatherForecastAlertOnly['rules']>>>
  hourOfDay: string | null
  setHourOfDay: Dispatch<SetStateAction<string | null>>
  setThresholdIsValid: Dispatch<SetStateAction<boolean>>
  triedToSubmit: boolean
}) => {
  const windUnit = unitSpeedDisplay()
  const tempUnit = unitTempDisplay()

  // Get Wind error
  let windError: null | string | boolean = null
  let windValidateStatus = ''
  let windHelp: string | boolean = ''

  if ('windGreaterThanOrEqualToUserUnits' in rule) {
    windError = AlertsValidators.wind({
      wind: rule.windGreaterThanOrEqualToUserUnits,
    })

    if (windError && triedToSubmit) {
      windValidateStatus = 'error'

      windHelp = helpTextErrorHelper(windError)
    }
  }

  // Get temperature error
  let temperatureError: null | string | boolean = null
  let temperatureValidateStatus = ''
  let temperatureHelp: string | boolean = ''

  if ('temperatureGreaterThanOrEqualToUserUnits' in rule) {
    const temperatureError = AlertsValidators.temperature({
      temperature: rule.temperatureGreaterThanOrEqualToUserUnits,
    })

    if (temperatureError && triedToSubmit) {
      temperatureValidateStatus = 'error'

      temperatureHelp = helpTextErrorHelper(temperatureError) || false
    }
  }

  if ('temperatureLessThanOrEqualToUserUnits' in rule) {
    const temperatureError = AlertsValidators.temperature({
      temperature: rule.temperatureLessThanOrEqualToUserUnits,
    })

    if (temperatureError && triedToSubmit) {
      temperatureValidateStatus = 'error'

      temperatureHelp = helpTextErrorHelper(temperatureError) || false
    }
  }

  const errorStyle = getErrorStyle()

  useEffect(() => {
    setThresholdIsValid(!windError && !temperatureError)
  }, [windError, temperatureError])

  const timeSelectOptions = Array.from({ length: 24 }, (_, i) => {
    let value = i.toString()

    if (value.length === 1) value = `0${value}`

    return { value, label: translate.dates.format(moment.tz().startOf('year').add(i, 'hours'), 'h:mm a') }
  })

  return (
    <>
      <TranslatePhraseWrapper>
        <translate.Phrases.banyanApp
          k="Send alerts at <selectInput/> to 7 days in the future when the following conditions are met"
          c={{
            selectInput: (
              <TranslateComponentWrapper>
                <Select
                  css={{ width: 125, margin: '0 5px' }}
                  onChange={(value) => {
                    setHourOfDay(value)
                  }}
                  data={timeSelectOptions}
                  value={typeof hourOfDay === 'string' ? hourOfDay : null}
                />
                <div />
              </TranslateComponentWrapper>
            ),
          }}
        />
      </TranslatePhraseWrapper>
      <MantineRadio.Group
        value={getRuleType(rule)}
        onChange={(val) => {
          setRule(() => {
            if (val === 'wind') {
              return {
                windGreaterThanOrEqualToUserUnits: null,
              }
            }

            if (val === 'rain') {
              return {
                hasRain: true,
              }
            }

            return {
              temperatureGreaterThanOrEqualToUserUnits: null,
              includeAllDevices: false,
              measurementHeights: ['aboveCanopyTemperature'],
            }
          })
        }}
      >
        {/**WIND */}
        <div css={{ marginTop: 20 }}>
          <Radio label={translate.phrases.banyanApp('Wind')} css={{ fontWeight: 500 }} value={'wind'} />
          <div
            css={{
              alignItems: 'center',
              display: 'flex',
              marginTop: 10,
              ...fadeInOutStylesMaker(
                getRuleType(rule) === AlertsSettings.weatherForecastAlertThresholdItems.WIND,
              ),
            }}
          >
            <TranslatePhraseWrapper>
              <translate.Phrases.banyanApp
                k="Send alert when wind speed is forecast to be greater than or equal to <numberInput/> {{windUnit}}"
                c={{
                  numberInput: (
                    <TranslateComponentWrapper>
                      <NumberInput
                        css={{ width: 80, marginLeft: 5, marginRight: 5 }}
                        onChange={(val) => {
                          if (getRuleType(rule) !== AlertsSettings.weatherForecastAlertThresholdItems.WIND)
                            return

                          setRule(() => ({
                            windGreaterThanOrEqualToUserUnits: val === '' ? undefined : val,
                          }))
                        }}
                        classNames={{ input: windValidateStatus ? errorStyle : undefined }}
                        value={isNumberOrNumericString(getRuleValue(rule)) ? Number(getRuleValue(rule)) : ''}
                        decimalScale={10}
                      />
                      <div />
                    </TranslateComponentWrapper>
                  ),
                }}
                v={{ windUnit }}
              />
            </TranslatePhraseWrapper>
          </div>
          {windHelp && <ErrorTextWrapper>{windHelp}</ErrorTextWrapper>}
        </div>
        {/**RAIN */}
        <div css={{ marginTop: 20, marginBottom: 20 }}>
          <Radio label={translate.phrases.banyanApp('Rain')} css={{ fontWeight: 500 }} value={'rain'} />
          <div
            css={{
              alignItems: 'center',
              display: 'flex',
              marginTop: 10,
              ...fadeInOutStylesMaker(
                getRuleType(rule) === AlertsSettings.weatherForecastAlertThresholdItems.RAIN,
              ),
            }}
          >
            <TranslatePhraseWrapper>
              <div>
                {translate.phrases.banyanApp('Send alert when rain is forecasted within the next 7 days.')}
              </div>
            </TranslatePhraseWrapper>
          </div>
        </div>
        {/**TEMPERATURE */}
        <div>
          <Radio
            label={translate.phrases.banyanApp('Temperature')}
            css={{ fontWeight: 500 }}
            value="temperature"
          />
          <div
            css={{
              alignItems: 'center',
              display: 'flex',
              marginTop: 10,
              ...fadeInOutStylesMaker(
                getRuleType(rule) === AlertsSettings.weatherForecastAlertThresholdItems.TEMPERATURE,
              ),
            }}
          >
            <TranslatePhraseWrapper>
              <translate.Phrases.banyanApp
                k="Send alert when temperature is forecast to be <selectInput/> <numberInput/>"
                c={{
                  selectInput: (
                    <TranslateComponentWrapper>
                      <Select
                        css={{ marginLeft: 5, marginRight: 5 }}
                        onChange={(val) => {
                          if (!val) return

                          if (val === '>=') {
                            setRule((prev) => {
                              if ('temperatureLessThanOrEqualToUserUnits' in prev) {
                                const { temperatureLessThanOrEqualToUserUnits, ...rest } = prev

                                return {
                                  ...rest,
                                  temperatureGreaterThanOrEqualToUserUnits:
                                    temperatureLessThanOrEqualToUserUnits,
                                }
                              }

                              return {
                                ...prev,
                                temperatureGreaterThanOrEqualToUserUnits: undefined,
                              }
                            })
                          } else if (val === '<=') {
                            setRule((prev) => {
                              if ('temperatureGreaterThanOrEqualToUserUnits' in prev) {
                                const { temperatureGreaterThanOrEqualToUserUnits, ...rest } = prev

                                return {
                                  ...rest,
                                  temperatureLessThanOrEqualToUserUnits:
                                    temperatureGreaterThanOrEqualToUserUnits,
                                }
                              }

                              return {
                                ...prev,
                                temperatureLessThanOrEqualToUserUnits: undefined,
                              }
                            })
                          }
                        }}
                        data={[
                          { value: '>=', label: translate.phrases.banyanApp('greater than or equal to') },
                          { value: '<=', label: translate.phrases.banyanApp('less than or equal to') },
                        ]}
                        value={getTemperatureOperator(rule)}
                      />
                      <div />
                    </TranslateComponentWrapper>
                  ),

                  numberInput: (
                    <TranslateComponentWrapper>
                      <NumberInput
                        value={isNumberOrNumericString(getRuleValue(rule)) ? Number(getRuleValue(rule)) : ''}
                        css={{ width: 80, marginLeft: 5, marginRight: 5 }}
                        onChange={(val) => {
                          if (
                            getRuleType(rule) !==
                            AlertsSettings.weatherForecastAlertThresholdItems.TEMPERATURE
                          )
                            return

                          setRule((prev) => {
                            if (getTemperatureOperator(rule) === '>=') {
                              return {
                                ...prev,
                                temperatureGreaterThanOrEqualToUserUnits: val === '' ? undefined : val,
                              }
                            } else {
                              return {
                                ...prev,
                                temperatureLessThanOrEqualToUserUnits: val === '' ? undefined : val,
                              }
                            }
                          })
                        }}
                        classNames={{ input: temperatureValidateStatus ? errorStyle : undefined }}
                        decimalScale={10}
                      />
                      <div>{tempUnit}</div>
                    </TranslateComponentWrapper>
                  ),
                }}
              />
            </TranslatePhraseWrapper>
          </div>
          {temperatureHelp && <ErrorTextWrapper>{temperatureHelp}</ErrorTextWrapper>}
        </div>
      </MantineRadio.Group>
    </>
  )
}
